/**
 * FrameContextMenu — Owner-only floating context menu triggered by long-press.
 *
 * Renders as a portal to document.body so it floats above the viewer.
 * Glass-morphism styling consistent with existing overlay buttons.
 * Dismisses on tap outside, Escape, or scroll.
 */

import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

export interface FrameContextMenuProps {
  isOpen: boolean
  position: { x: number; y: number }
  frameIndex: number
  frameFile: string
  sectionName: string
  onRescanArea: () => void
  onFrameInfo: () => void
  onClose: () => void
}

export function FrameContextMenu({
  isOpen,
  position,
  frameIndex,
  frameFile: _frameFile,
  sectionName,
  onRescanArea,
  onFrameInfo,
  onClose,
}: FrameContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Dismiss on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // Dismiss on scroll
  useEffect(() => {
    if (!isOpen) return
    const handleScroll = () => onClose()
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen, onClose])

  // Dismiss on click outside
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        e.stopPropagation()
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  // Position the menu near the touch point, clamped to viewport
  const menuWidth = 220
  const menuHeight = 120
  const padding = 12
  const vw = window.innerWidth
  const vh = window.innerHeight

  let left = position.x - menuWidth / 2
  let top = position.y - menuHeight - 16 // Above the finger

  // Clamp horizontal
  if (left < padding) left = padding
  if (left + menuWidth > vw - padding) left = vw - padding - menuWidth

  // If menu would go above viewport, show below instead
  if (top < padding) top = position.y + 24

  // Clamp vertical
  if (top + menuHeight > vh - padding) top = vh - padding - menuHeight

  const menu = (
    <div
      onClick={handleBackdropClick}
      onTouchEnd={(e) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
          e.stopPropagation()
          onClose()
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999999,
      }}
    >
      <div
        ref={menuRef}
        style={{
          position: 'absolute',
          left,
          top,
          width: menuWidth,
          background: 'rgba(20, 20, 30, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 14,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          animation: 'contextMenuFadeIn 0.15s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '8px 14px 4px',
            fontSize: 11,
            color: 'rgba(255,255,255,0.45)',
            fontWeight: 500,
            letterSpacing: 0.3,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {sectionName || 'Section'} · Frame {frameIndex + 1}
        </div>

        {/* Menu Items */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRescanArea()
          }}
          style={menuItemStyle}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span style={{ fontSize: 16, marginRight: 8 }}>📷</span>
          Rescan this area
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            onFrameInfo()
          }}
          style={{ ...menuItemStyle, borderBottom: 'none' }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span style={{ fontSize: 16, marginRight: 8 }}>ℹ️</span>
          Frame info
        </button>
      </div>

      {/* Inline keyframe animation */}
      <style>{`
        @keyframes contextMenuFadeIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  )

  return createPortal(menu, document.body)
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '11px 14px',
  border: 'none',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  background: 'transparent',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.12s',
}
